import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart3, Users, ChevronLeft, ChevronRight, Search, Loader2, Info,
  Layers, Filter, X, RefreshCw, Trophy, Building2, Crown, TrendingDown, TrendingUp,
  Download, Share2, Medal,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RTooltip,
  BarChart, Bar, XAxis, YAxis,
} from "recharts";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/AppShell";
import { RecruiterAvatar, rdPhotoSrc } from "@/components/RecruiterAvatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toPng } from "html-to-image";

/* ─────────────────────────── Constantes & helpers ─────────────────────────── */
const PERIODS = [
  { value: "day", label: "Jour" },
  { value: "week", label: "Semaine" },
  { value: "month", label: "Mois" },
  { value: "quarter", label: "Trimestre" },
  { value: "year", label: "Année" },
  { value: "all", label: "Tout" },
];

const CANAL_COLORS = {
  sites_prives: "rgba(238,114,46,0.88)",
  rue_fixe: "rgba(47,111,237,0.85)",
  rue_itin: "rgba(14,165,165,0.85)",
  pap: "rgba(139,92,246,0.85)",
  inconnu: "rgba(24,24,24,0.22)",
};
const GENDER_COLORS = { H: "rgba(47,111,237,0.85)", F: "rgba(238,114,46,0.88)", Autre: "rgba(24,24,24,0.30)" };
const QUALITY_COLORS = {
  approuves: "rgba(31,157,85,0.9)",
  en_attente: "rgba(238,114,46,0.85)",
  invalides: "rgba(192,57,43,0.85)",
  annules: "rgba(24,24,24,0.28)",
};
const TEAM_BAR = "rgba(238,114,46,0.85)";

const _pct = (r) => (r == null ? "—" : `${Math.round(r * 100)} %`);
const _eur = (v) => (v == null ? "—" : `${Number(v).toFixed(2).replace(".", ",")} €`);
const _num = (v) => (v == null ? "—" : String(v));
const _age = (v) => (v == null ? "—" : `${String(v).replace(".", ",")} ans`);
const _ratio = (v) => (v == null ? "—" : Number(v).toFixed(2).replace(".", ","));
const _heures = (v) => (v == null ? "—" : `${Number(v).toFixed(2).replace(".", ",")} h`);

const _wcTone = (r) => (r == null ? "default" : r <= 0.15 ? "good" : r <= 0.25 ? "warn" : "bad");
const _attrTone = (r, t) => (r == null ? "default" : r <= t ? "good" : r <= t * 1.5 ? "warn" : "bad");

/* ─────────────────────────── Petits composants UI ─────────────────────────── */
function KpiTile({ label, value, sub, tone = "default", testid }) {
  const toneCls = {
    default: "border-[var(--border-soft)] bg-white",
    good: "border-[rgba(31,157,85,0.40)] bg-[rgba(31,157,85,0.06)]",
    warn: "border-[rgba(234,179,8,0.45)] bg-[rgba(234,179,8,0.08)]",
    bad: "border-[rgba(155,34,38,0.40)] bg-[rgba(155,34,38,0.06)]",
    accent: "border-[rgba(238,114,46,0.40)] bg-[rgba(238,114,46,0.06)]",
  }[tone] || "border-[var(--border-soft)] bg-white";
  return (
    <div className={`min-w-0 rounded-xl border p-3 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase leading-tight tracking-wide text-muted-ink">{label}</div>
      <div className="mt-0.5 truncate text-lg font-extrabold leading-tight text-ink">{value}</div>
      {sub ? <div className="mt-0.5 truncate text-[10px] text-muted-ink">{sub}</div> : null}
    </div>
  );
}

function KpiMemberStat({ label, value, tone = "default" }) {
  const c = { default: "text-ink", good: "text-[#0F5A2F]", warn: "text-[#8A6D1B]", bad: "text-[#9B2226]" }[tone] || "text-ink";
  return (
    <div className="flex items-center justify-between gap-1 rounded-lg bg-[var(--surface-2)] px-2 py-1">
      <span className="truncate text-muted-ink">{label}</span>
      <span className={`shrink-0 font-bold ${c}`}>{value}</span>
    </div>
  );
}

function ChartTip({ active, payload, suffix }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  const pl = p.payload || {};
  return (
    <div className="rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5 text-xs shadow-[var(--shadow-sm)]">
      <span className="font-semibold text-ink">{pl.label || p.name}</span>
      <span className="text-muted-ink"> · {p.value}{suffix || ""}{pl.pct != null ? ` (${pl.pct}%)` : ""}</span>
    </div>
  );
}

// Donut générique avec légende + valeur centrale.
function Donut({ title, icon: Icon, data, centerValue, centerLabel, testid }) {
  const pieData = data.filter((r) => r.value > 0);
  const empty = pieData.length === 0;
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-3 shadow-[var(--shadow-sm)]" data-testid={testid}>
      <div className="mb-1 flex items-center gap-1.5 text-sm font-extrabold text-ink">
        {Icon ? <Icon className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" /> : null}
        <span className="truncate">{title}</span>
      </div>
      <div className="relative" style={{ width: "100%", height: 180 }}>
        {empty ? (
          <div className="grid h-full place-items-center text-xs text-muted-ink">Aucune donnée</div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={180} minWidth={0}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="label" innerRadius={52} outerRadius={76} paddingAngle={2} stroke="none">
                  {pieData.map((r) => <Cell key={r.key} fill={r.color} />)}
                </Pie>
                <RTooltip content={<ChartTip suffix={title.toLowerCase().includes("canal") ? " BS" : ""} />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl font-extrabold leading-none text-ink">{centerValue}</span>
              <span className="mt-0.5 text-[10px] text-muted-ink">{centerLabel}</span>
            </div>
          </>
        )}
      </div>
      {!empty && (
        <div className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1">
          {data.map((r) => (
            <span key={r.key} className="inline-flex items-center gap-1.5 text-[11px] text-muted-ink">
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: r.color }} />
              {r.label} · <b className="text-ink">{r.value}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────── Podium gamifié ───────────────────────────── */
// Métadonnées visuelles : or / argent / bronze (hauteur de marche décroissante).
const PODIUM_META = [
  { ring: "#E9B800", bg: "linear-gradient(180deg, rgba(233,184,0,0.28), rgba(233,184,0,0.04))", ped: 78, av: 74, label: "Or" },
  { ring: "#9AA3AF", bg: "linear-gradient(180deg, rgba(154,163,175,0.30), rgba(154,163,175,0.05))", ped: 56, av: 60, label: "Argent" },
  { ring: "#C77F3B", bg: "linear-gradient(180deg, rgba(199,127,59,0.28), rgba(199,127,59,0.05))", ped: 40, av: 60, label: "Bronze" },
];

// KPIs classables. `dir` = sens du classement : "desc" (plus haut = meilleur) ou
// "asc" (plus bas = meilleur). `attr` = nécessite les données d'attrition (Reporting).
const PODIUM_KPIS = [
  { key: "bulletins_comptes", label: "Bulletins", dir: "desc", attr: false, fmt: (v) => `${_num(v)} BS` },
  { key: "taux_reel", label: "Taux réel", dir: "desc", attr: false, fmt: (v) => `${_ratio(v)} BS/h` },
  { key: "validation_rate", label: "Validation", dir: "desc", attr: false, fmt: _pct },
  { key: "don_moyen", label: "Don moyen", dir: "desc", attr: false, fmt: _eur },
  { key: "average_age", label: "Âge moyen", dir: "desc", attr: false, fmt: _age },
  { key: "welcome_call_rate", label: "Annul. WC", dir: "asc", attr: false, fmt: _pct },
  { key: "pre_attrition_rate", label: "Pré-attrition", dir: "asc", attr: true, fmt: _pct },
  { key: "attrition_rate", label: "Attrition", dir: "asc", attr: true, fmt: _pct },
];

// Accès à la valeur d'un KPI pour un "entrant" (collaborateur OU équipe).
function entrantValue(ent, kpi) {
  if (kpi.attr) return ent.attrSource?.reliable ? (ent.attrSource?.[kpi.key] ?? null) : null;
  const v = ent.kpiSource?.[kpi.key];
  return v == null ? null : v;
}

function PodiumRow({ rank, ent, value }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-[var(--surface-2)]" data-testid={`kpis-podium-row-${rank}`}>
      <span className="w-5 shrink-0 text-center text-xs font-extrabold text-muted-ink">{rank}</span>
      <RecruiterAvatar name={ent.name} src={ent.photoSrc} size={30} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold text-ink">{ent.name}</div>
        <div className="truncate text-[10px] font-semibold text-muted-ink">{ent.sub}</div>
      </div>
      <span className="shrink-0 text-sm font-extrabold text-[var(--shariti-orange)]">{value}</span>
    </div>
  );
}

/* ── Badge du mois — carte PNG partageable (WhatsApp, etc.) pour le 1er ──────── */
function BadgeDialog({ open, onOpenChange, champion, kpi, periodLabel, mode }) {
  const badgeRef = useRef(null);
  const [busy, setBusy] = useState(false);
  if (!champion) return null;

  const ent = champion.ent;
  const value = kpi.fmt(champion.v);
  const title = mode === "teams" ? "ÉQUIPE DU MOIS" : "CHAMPION DU MOIS";
  const safe = (s) => String(s || "").replace(/[^\w]+/g, "_").replace(/_+/g, "_").slice(0, 50);
  const filename = `badge-${safe(ent.name)}-${safe(periodLabel)}.png`;

  const render = async () => {
    // Petite pause pour s'assurer que l'avatar (image same-origin) est bien peint.
    await new Promise((r) => setTimeout(r, 120));
    return toPng(badgeRef.current, { pixelRatio: 2.5, cacheBust: true, backgroundColor: "#141414" });
  };

  const doDownload = async () => {
    try {
      setBusy(true);
      const url = await render();
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Badge téléchargé.");
    } catch {
      toast.error("Impossible de générer le badge.");
    } finally { setBusy(false); }
  };

  const doShare = async () => {
    try {
      setBusy(true);
      const url = await render();
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], filename, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title, text: `${title} — ${ent.name} · ${kpi.label} : ${value}` });
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        toast.info("Partage direct indisponible : badge téléchargé, partagez-le sur WhatsApp.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Partage impossible.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="kpis-badge-dialog">
        <DialogHeader>
          <DialogTitle>Badge du mois</DialogTitle>
          <DialogDescription>À télécharger ou partager (WhatsApp…) pour féliciter {mode === "teams" ? "l'équipe" : "le collaborateur"} n°1.</DialogDescription>
        </DialogHeader>

        {/* Carte badge (capturée en PNG) */}
        <div
          ref={badgeRef}
          data-testid="kpis-badge-card"
          style={{ width: 340, background: "radial-gradient(circle at 50% -10%, #343038, #141414 70%)" }}
          className="relative mx-auto overflow-hidden rounded-2xl px-5 pb-5 pt-6 text-center text-white"
        >
          {/* Halo doré décoratif */}
          <div style={{ background: "radial-gradient(circle, rgba(233,184,0,0.26), transparent 70%)" }}
            className="pointer-events-none absolute -top-16 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full" />
          <div className="relative">
            <div className="flex items-center justify-center gap-1.5">
              <Crown className="h-5 w-5" style={{ color: "#E9B800" }} fill="#E9B800" />
              <span className="text-xs font-extrabold uppercase tracking-[0.18em]" style={{ color: "#E9B800" }}>{title}</span>
            </div>
            <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-white/55">{periodLabel || ""}</div>

            <div className="mt-4 flex justify-center">
              <div className="rounded-full" style={{ padding: 4, background: "linear-gradient(145deg, #F5D76E, #C77F3B)" }}>
                <RecruiterAvatar name={ent.name} src={ent.photoSrc} size={92} className="ring-2 ring-[#141414]" />
              </div>
            </div>

            <div className="mt-3 break-words px-2 text-xl font-extrabold leading-tight">{ent.name}</div>
            <div className="mt-0.5 text-[12px] font-medium text-white/60">{ent.sub}</div>

            <div className="mt-4 inline-flex flex-col items-center rounded-xl px-5 py-2.5"
              style={{ background: "linear-gradient(145deg, rgba(238,114,46,0.95), rgba(214,90,20,0.95))" }}>
              <span className="text-2xl font-extrabold leading-none">{value}</span>
              <span className="mt-1 text-[10px] font-bold uppercase tracking-wide text-white/85">{kpi.label}</span>
            </div>

            <div className="mt-5 flex items-center justify-center gap-1.5 border-t border-white/10 pt-3 text-[10px] font-extrabold uppercase tracking-[0.22em] text-white/70">
              <Medal className="h-3.5 w-3.5" style={{ color: "#E9B800" }} /> Shariti · Face to Face
            </div>
          </div>
        </div>

        <div className="mt-3 flex gap-2">
          <Button variant="outline" className="flex-1 gap-1.5" onClick={doDownload} disabled={busy} data-testid="kpis-badge-download">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Télécharger
          </Button>
          <Button className="flex-1 gap-1.5 bg-[var(--shariti-orange)] text-white hover:bg-[var(--shariti-orange)]/90" onClick={doShare} disabled={busy} data-testid="kpis-badge-share">
            <Share2 className="h-4 w-4" /> Partager
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PodiumSection({ members, byTeam, allowTeams, kpiKey, setKpiKey, attrLoading, canLoadAttr, onLoadAttr, periodLabel }) {
  const kpi = PODIUM_KPIS.find((k) => k.key === kpiKey) || PODIUM_KPIS[0];
  const [mode, setMode] = useState("members"); // "members" | "teams"
  const [badgeOpen, setBadgeOpen] = useState(false);

  // Si le classement par équipe n'est plus possible (filtre équipe, <2 équipes) → revient aux collaborateurs.
  useEffect(() => { if (!allowTeams && mode === "teams") setMode("members"); }, [allowTeams, mode]);

  const entrants = useMemo(() => {
    if (mode === "teams") {
      return (byTeam || []).map((bt) => ({
        id: bt.chef_id || "none",
        name: bt.chef_nom,
        sub: `${bt.size} collaborateur${bt.size > 1 ? "s" : ""}`,
        photoSrc: undefined, // avatar = initiales de l'équipe
        kpiSource: bt.team || {},
        attrSource: (bt.team || {}).attrition,
      }));
    }
    return (members || []).map((m) => ({
      id: m.rd_id,
      name: m.rd_name,
      sub: `${m.chef_nom} · ${m.canal_label}`,
      photoSrc: rdPhotoSrc(m),
      kpiSource: m,
      attrSource: m.attrition,
    }));
  }, [mode, members, byTeam]);

  const ranked = useMemo(() => {
    const arr = entrants.map((ent) => ({ ent, v: entrantValue(ent, kpi) })).filter((x) => x.v != null);
    arr.sort((a, b) => (kpi.dir === "asc" ? a.v - b.v : b.v - a.v));
    return arr;
  }, [entrants, kpi]);

  const top = ranked.slice(0, 3);
  const rest = ranked.slice(3);
  const visual = top.length >= 3 ? [top[1], top[0], top[2]] : (top.length === 2 ? [top[1], top[0]] : top);
  const needAttr = kpi.attr && ranked.length === 0;
  const lowBest = kpi.dir === "asc";
  const isTeams = mode === "teams";

  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-gradient-to-b from-[rgba(238,114,46,0.05)] to-white p-3 shadow-[var(--shadow-sm)]" data-testid="kpis-podium">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-sm font-extrabold text-ink">
          <Trophy className="h-4 w-4 shrink-0 text-[#E9B800]" />
          <span>{isTeams ? "Podium des équipes" : "Podium"} — {kpi.label}</span>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[10px] font-semibold text-muted-ink">
          {lowBest ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
          {lowBest ? "le plus bas gagne" : "le plus haut gagne"}
        </span>

        <div className="ml-auto flex items-center gap-2">
          {allowTeams && (
            <div className="inline-flex rounded-full bg-[var(--surface-2)] p-0.5" data-testid="kpis-podium-mode">
              <button onClick={() => setMode("members")} data-testid="kpis-podium-mode-members"
                className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors ${!isTeams ? "bg-white text-ink shadow-[var(--shadow-sm)]" : "text-muted-ink"}`}>
                Collaborateurs
              </button>
              <button onClick={() => setMode("teams")} data-testid="kpis-podium-mode-teams"
                className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors ${isTeams ? "bg-white text-ink shadow-[var(--shadow-sm)]" : "text-muted-ink"}`}>
                Équipes
              </button>
            </div>
          )}
          <Button size="sm" variant="outline" className="h-8 gap-1.5" disabled={ranked.length < 1}
            onClick={() => setBadgeOpen(true)} data-testid="kpis-podium-badge-btn">
            <Medal className="h-3.5 w-3.5 text-[#E9B800]" /> Badge du mois
          </Button>
        </div>
      </div>

      {/* Sélecteur de KPI */}
      <div className="mb-3 flex flex-wrap gap-1.5" data-testid="kpis-podium-selector">
        {PODIUM_KPIS.map((k) => (
          <button
            key={k.key}
            onClick={() => setKpiKey(k.key)}
            data-testid={`kpis-podium-kpi-${k.key}`}
            className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors ${k.key === kpiKey
              ? "bg-[var(--shariti-orange)] text-white"
              : "bg-white text-ink ring-1 ring-[var(--border-soft)] hover:bg-[var(--surface-2)]"}`}
          >
            {k.label}
          </button>
        ))}
      </div>

      {needAttr ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-[var(--border-soft)] bg-white px-4 py-6 text-center">
          {attrLoading ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-muted-ink"><Loader2 className="h-4 w-4 animate-spin" /> Chargement de l'attrition…</span>
          ) : canLoadAttr ? (
            <>
              <span className="text-sm text-muted-ink">L'attrition n'a pas pu être chargée.</span>
              <Button variant="outline" size="sm" className="h-8 gap-1" onClick={onLoadAttr} data-testid="kpis-podium-load-attr">
                <RefreshCw className="h-3.5 w-3.5" /> Réessayer
              </Button>
            </>
          ) : (
            <span className="text-sm text-muted-ink">Recul insuffisant pour classer l'attrition sur cette sélection.</span>
          )}
        </div>
      ) : ranked.length < 2 ? (
        <div className="rounded-xl border border-dashed border-[var(--border-soft)] bg-white px-4 py-6 text-center text-sm text-muted-ink" data-testid="kpis-podium-empty">
          {isTeams ? "Pas assez d'équipes avec cette donnée pour établir un podium." : "Pas assez de collaborateurs avec cette donnée pour établir un podium."}
        </div>
      ) : (
        <>
          {/* Top 3 — marches 2 · 1 · 3 */}
          <div className="flex items-end justify-center gap-3 sm:gap-6">
            {visual.map((entry) => {
              const rank = top.indexOf(entry); // 0,1,2
              const mt = PODIUM_META[rank];
              const ent = entry.ent;
              return (
                <div key={ent.id} className="flex min-w-0 flex-1 flex-col items-center" style={{ maxWidth: 150 }} data-testid={`kpis-podium-rank-${rank + 1}`}>
                  {rank === 0 && <Crown className="mb-0.5 h-5 w-5 text-[#E9B800]" fill="#E9B800" />}
                  <div className="relative shrink-0 rounded-full" style={{ padding: 3, background: mt.ring }}>
                    <RecruiterAvatar name={ent.name} src={ent.photoSrc} size={mt.av} className="ring-2 ring-white" testid={`kpis-podium-avatar-${rank + 1}`} />
                    <span className="absolute -bottom-1 -right-1 grid h-6 w-6 place-items-center rounded-full border-2 border-white text-[11px] font-extrabold text-white shadow-[var(--shadow-sm)]" style={{ background: mt.ring }}>{rank + 1}</span>
                  </div>
                  <div className="mt-2 w-full break-words text-center text-[12px] font-bold leading-tight text-ink">{ent.name}</div>
                  <div className="mt-0.5 w-full truncate text-center text-[10px] font-semibold text-muted-ink">{ent.sub}</div>
                  <div className="mt-0.5 text-[12px] font-extrabold text-[var(--shariti-orange)]">{kpi.fmt(entry.v)}</div>
                  <div className="mt-2 flex w-full items-start justify-center rounded-t-lg pt-1 text-[10px] font-extrabold uppercase tracking-wide"
                    style={{ height: mt.ped, background: mt.bg, borderTop: `3px solid ${mt.ring}`, color: mt.ring }}>
                    {mt.label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Classement complet (4e et +) */}
          {rest.length > 0 && (
            <div className="mt-3 border-t border-[var(--border-soft)] pt-2" data-testid="kpis-podium-rest">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-ink">Classement</div>
              <div className="max-h-64 space-y-0.5 overflow-y-auto pr-1">
                {rest.map((entry, i) => (
                  <PodiumRow key={entry.ent.id} rank={i + 4} ent={entry.ent} value={kpi.fmt(entry.v)} />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <BadgeDialog
        open={badgeOpen}
        onOpenChange={setBadgeOpen}
        champion={ranked[0] || null}
        kpi={kpi}
        periodLabel={periodLabel}
        mode={mode}
      />
    </div>
  );
}

/* ─────────────────────────────────── Page ─────────────────────────────────── */
export default function AdminKpis() {
  const [period, setPeriod] = useState("month");
  const [offset, setOffset] = useState(0);
  const [chefId, setChefId] = useState("all");
  const [canal, setCanal] = useState("tous");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [podiumKpi, setPodiumKpi] = useState("bulletins_comptes");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attrState, setAttrState] = useState("idle"); // idle | loading | done | error
  const sigRef = useRef(0);

  // Debounce de la recherche (évite une requête à chaque frappe).
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 350);
    return () => clearTimeout(t);
  }, [qInput]);

  // Réinitialise la navigation temporelle quand on change de granularité.
  useEffect(() => { setOffset(0); }, [period]);

  const baseParams = useMemo(() => ({
    period, offset,
    chef_id: chefId === "all" ? "" : chefId,
    canal, q,
  }), [period, offset, chefId, canal, q]);

  const load = useCallback(async () => {
    const mySig = ++sigRef.current;
    setLoading(true);
    setAttrState("idle");
    try {
      const { data: d1 } = await api.get("/pointage/admin/kpis-global", { params: { ...baseParams, attrition: 0 } });
      if (sigRef.current !== mySig) return;
      setData(d1);
      setLoading(false);
      // Phase 2 — attrition (Reporting) : auto seulement si peu de collaborateurs.
      const n = d1?.totals?.members_all || 0;
      if (n > 0) {
        // Attrition désormais rapide (1 appel groupé côté Reporting + cache) :
        // on la charge TOUJOURS automatiquement en arrière-plan.
        setAttrState("loading");
        try {
          const { data: d2 } = await api.get("/pointage/admin/kpis-global", { params: { ...baseParams, attrition: 1 } });
          if (sigRef.current !== mySig) return;
          setData(d2);
          setAttrState("done");
        } catch {
          if (sigRef.current === mySig) setAttrState("error");
        }
      }
    } catch {
      if (sigRef.current !== mySig) return;
      setLoading(false);
      toast.error("Impossible de charger les KPIs.");
    }
  }, [baseParams]);

  useEffect(() => { load(); }, [load]);

  const loadAttrition = async () => {
    const mySig = sigRef.current;
    setAttrState("loading");
    try {
      const { data: d2 } = await api.get("/pointage/admin/kpis-global", { params: { ...baseParams, attrition: 1 } });
      if (sigRef.current !== mySig) return;
      setData(d2);
      setAttrState("done");
    } catch {
      if (sigRef.current === mySig) { setAttrState("error"); toast.error("Attrition indisponible pour le moment."); }
    }
  };

  const team = data?.team || {};
  const attr = team.attrition || {};
  const t3 = attr.target_3m ?? 0.11;
  const t12 = attr.target_12m ?? 0.15;
  const attrLoading = attrState === "loading";
  const attrVal = (val) => (attrLoading ? "…" : attr.reliable ? _pct(val) : "—");
  const attrSub = (target) => (attrLoading ? "chargement…" : attr.reliable ? `cible ≤ ${_pct(target)}` : "recul insuffisant");

  const chefs = data?.chefs || [];
  const canaux = data?.canaux || [];
  const members = data?.members || [];
  const byTeam = data?.by_team || [];
  const byCanal = data?.by_canal || [];

  // Données graphiques (mémoïsées).
  const genderData = useMemo(() => {
    const g = team.gender || {};
    return [
      { key: "H", label: "Hommes", value: g.H || 0, color: GENDER_COLORS.H },
      { key: "F", label: "Femmes", value: g.F || 0, color: GENDER_COLORS.F },
      { key: "Autre", label: "Autre", value: g.Autre || 0, color: GENDER_COLORS.Autre },
    ];
  }, [team.gender]);

  const canalData = useMemo(() => byCanal.map((c) => ({
    key: c.key, label: c.label, value: c.bulletins || 0, members: c.members,
    color: CANAL_COLORS[c.key] || CANAL_COLORS.inconnu,
  })), [byCanal]);

  const qualityData = useMemo(() => ([
    { key: "approuves", label: "Approuvés", value: team.approuves || 0, color: QUALITY_COLORS.approuves },
    { key: "en_attente", label: "En attente", value: team.en_attente || 0, color: QUALITY_COLORS.en_attente },
    { key: "invalides", label: "Invalides (WC)", value: team.invalides || 0, color: QUALITY_COLORS.invalides },
    { key: "annules", label: "Annulés", value: team.annules || 0, color: QUALITY_COLORS.annules },
  ]), [team.approuves, team.en_attente, team.invalides, team.annules]);

  const teamBarData = useMemo(() => byTeam.map((bt) => ({
    label: bt.chef_nom, chef_id: bt.chef_id, value: bt.team?.bulletins_comptes || 0,
  })), [byTeam]);

  const totalGender = (team.gender?.H || 0) + (team.gender?.F || 0) + (team.gender?.Autre || 0);
  const femPct = totalGender ? Math.round(((team.gender?.F || 0) / totalGender) * 100) : null;
  const totalBS = team.bulletins_comptes || 0;
  const dominantCanal = canalData.filter((c) => c.value > 0).sort((a, b) => b.value - a.value)[0];

  const filtersActive = chefId !== "all" || canal !== "tous" || !!q;
  const resetFilters = () => { setChefId("all"); setCanal("tous"); setQInput(""); setQ(""); };

  return (
    <>
      <PageHeader
        title="KPIs équipes"
        subtitle="Vue globale de toute l'entreprise — mêmes indicateurs que les responsables, avec filtres par équipe, par canal et recherche."
      >
        {/* Sélecteur de période + navigation temporelle */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-1.5" data-testid="kpis-periods">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                data-testid={`kpis-period-${p.value}`}
                className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${period === p.value
                  ? "bg-[var(--shariti-orange)] text-white"
                  : "bg-white/70 text-ink hover:bg-white"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-full bg-white/70 p-1" style={{ maxWidth: 360 }}>
            <button onClick={() => setOffset((o) => o - 1)} data-testid="kpis-prev"
              aria-label="Période précédente"
              className="grid h-7 w-7 place-items-center rounded-full text-ink hover:bg-[var(--surface-2)]">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="flex-1 truncate px-2 text-center text-xs font-bold text-ink" data-testid="kpis-label">{data?.label || "…"}</span>
            <button onClick={() => data?.can_forward && setOffset((o) => o + 1)} disabled={!data?.can_forward}
              data-testid="kpis-next" aria-label="Période suivante"
              className="grid h-7 w-7 place-items-center rounded-full text-ink hover:bg-[var(--surface-2)] disabled:opacity-40">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </PageHeader>

      <PageBody>
        {/* ── Barre de filtres ───────────────────────────────────────────── */}
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="kpis-filters">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-ink">
            <Filter className="h-4 w-4" /> Filtres
          </div>
          <Select value={chefId} onValueChange={setChefId}>
            <SelectTrigger className="h-9 w-[200px]" data-testid="kpis-filter-equipe">
              <SelectValue placeholder="Toutes les équipes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les équipes</SelectItem>
              {chefs.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`kpis-equipe-opt-${c.id}`}>
                  {c.nom} · {c.size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={canal} onValueChange={setCanal}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="kpis-filter-canal">
              <SelectValue placeholder="Tous les canaux" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tous">Tous les canaux</SelectItem>
              {canaux.map((c) => (
                <SelectItem key={c.key} value={c.key} data-testid={`kpis-canal-opt-${c.key}`}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
            <Input
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Rechercher un collaborateur…"
              className="h-9 w-[240px] pl-8"
              data-testid="kpis-filter-search"
            />
          </div>
          {filtersActive && (
            <Button variant="ghost" size="sm" onClick={resetFilters} className="h-9 gap-1 text-muted-ink" data-testid="kpis-filter-reset">
              <X className="h-4 w-4" /> Réinitialiser
            </Button>
          )}
          <div className="ml-auto text-xs font-semibold text-muted-ink" data-testid="kpis-scope-count">
            {loading ? "…" : `${data?.totals?.members || 0} collaborateur${(data?.totals?.members || 0) > 1 ? "s" : ""} · ${data?.totals?.teams || 0} équipe${(data?.totals?.teams || 0) > 1 ? "s" : ""}`}
          </div>
        </div>

        {loading && !data ? (
          <div className="space-y-4" data-testid="kpis-loading">
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-5">
              {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-2xl" />)}
            </div>
          </div>
        ) : (data?.totals?.members_all || 0) === 0 ? (
          <div className="rounded-2xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-10 text-center" data-testid="kpis-empty">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-[rgba(238,114,46,0.10)]">
              <BarChart3 className="h-6 w-6 text-[var(--shariti-orange)]" />
            </div>
            <h2 className="text-base font-extrabold text-ink">Aucune équipe à afficher</h2>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-ink">
              Aucun collaborateur n'est rattaché à une équipe pour cette sélection. Constituez des équipes dans « Équipes » pour voir les KPIs ici.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* ── Agrégat global ──────────────────────────────────────────── */}
            <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-3 shadow-[var(--shadow-sm)]" data-testid="kpis-global">
              <div className="mb-2 flex items-center gap-1.5 text-sm font-extrabold text-ink">
                <Layers className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
                <span className="truncate">
                  {chefId === "all" ? "Toute l'entreprise" : `Équipe ${byTeam[0]?.chef_nom || ""}`}
                  {` · ${data?.totals?.members || 0} collaborateur${(data?.totals?.members || 0) > 1 ? "s" : ""}`}
                </span>
                {attrLoading && (
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-muted-ink">
                    <Loader2 className="h-3 w-3 animate-spin" /> attrition…
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-5">
                <KpiTile testid="kpi-g-bulletins" label="Bulletins comptés" value={_num(team.bulletins_comptes)} sub={`${_num(team.bulletins_signes)} signés`} tone="accent" />
                <KpiTile testid="kpi-g-tauxreel" label="Taux réel (BS/h)" value={_ratio(team.taux_reel)}
                  sub={team.heures == null ? "bulletins nets / heure"
                    : (team.heures_formation
                      ? `${_heures(team.heures_terrain)} terrain · ${_heures(team.heures_formation)} form. exclue`
                      : `${_heures(team.heures)} travaillées`)} tone="accent" />
                <KpiTile testid="kpi-g-validation" label="Taux de validation" value={_pct(team.validation_rate)} sub="bulletins validés" tone="good" />
                <KpiTile testid="kpi-g-don" label="Don moyen" value={_eur(team.don_moyen)} sub={`${_num(team.don_n)} dons`} />
                <KpiTile testid="kpi-g-age" label="Âge moyen" value={_age(team.average_age)} sub={team.mature_share != null ? `${_pct(team.mature_share)} > 25 ans` : null} />
                <KpiTile testid="kpi-g-wc" label="Annul. welcome call" value={_pct(team.welcome_call_rate)} sub="cible ≤ 15 %" tone={_wcTone(team.welcome_call_rate)} />
                <KpiTile testid="kpi-g-preattr" label="Pré-attrition ≤3 m" value={attrVal(attr.pre_attrition_rate)} sub={attrSub(t3)} tone={attr.reliable && !attrLoading ? _attrTone(attr.pre_attrition_rate, t3) : "default"} />
                <KpiTile testid="kpi-g-attr" label="Attrition 12 m" value={attrVal(attr.attrition_rate)} sub={attrSub(t12)} tone={attr.reliable && !attrLoading ? _attrTone(attr.attrition_rate, t12) : "default"} />
                <KpiTile testid="kpi-g-gender" label="Répartition H / F" value={`${team.gender?.H || 0} / ${team.gender?.F || 0}`} sub="hommes / femmes" />
                <KpiTile testid="kpi-g-heures" label="Heures terrain" value={_heures(team.heures_terrain)} sub={team.heures_formation ? `+ ${_heures(team.heures_formation)} formation` : "hors formation"} />
              </div>
              {attrState === "error" && (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-[rgba(155,34,38,0.06)] px-3 py-2 text-[11px] text-[#9B2226]">
                  <Info className="h-3.5 w-3.5 shrink-0" />
                  L'attrition n'a pas pu être chargée.
                  <Button variant="outline" size="sm" className="h-7 gap-1" onClick={loadAttrition} data-testid="kpis-load-attrition">
                    <RefreshCw className="h-3 w-3" /> Réessayer
                  </Button>
                </div>
              )}
            </div>

            {/* ── Podium (KPI sélectionnable · Collaborateurs / Équipes) ───── */}
            <PodiumSection
              members={members}
              byTeam={byTeam}
              allowTeams={chefId === "all" && byTeam.length >= 2}
              kpiKey={podiumKpi}
              setKpiKey={setPodiumKpi}
              attrLoading={attrLoading}
              canLoadAttr={attrState === "error"}
              onLoadAttr={loadAttrition}
              periodLabel={data?.label}
            />

            {/* ── Graphiques « ronds » ────────────────────────────────────── */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <Donut
                testid="kpis-donut-gender" title="Répartition H / F" icon={Users}
                data={genderData}
                centerValue={femPct != null ? `${femPct}%` : "—"} centerLabel="femmes"
              />
              <Donut
                testid="kpis-donut-canal" title="Bulletins par canal" icon={Layers}
                data={canalData}
                centerValue={_num(totalBS)} centerLabel={dominantCanal ? dominantCanal.label.toLowerCase() : "bulletins"}
              />
              <Donut
                testid="kpis-donut-quality" title="Qualité des bulletins" icon={BarChart3}
                data={qualityData}
                centerValue={_pct(team.validation_rate)} centerLabel="validés"
              />
            </div>

            {/* ── Comparatif par équipe (si plusieurs équipes) ────────────── */}
            {teamBarData.length > 1 && (
              <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-3 shadow-[var(--shadow-sm)]" data-testid="kpis-team-bar">
                <div className="mb-2 flex items-center gap-1.5 text-sm font-extrabold text-ink">
                  <BarChart3 className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" /> Bulletins comptés par équipe
                </div>
                <ResponsiveContainer width="100%" height={Math.max(140, teamBarData.length * 42)} minWidth={0}>
                  <BarChart layout="vertical" data={teamBarData} margin={{ top: 4, right: 32, left: 8, bottom: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 12, fill: "var(--ink)" }} axisLine={false} tickLine={false} />
                    <RTooltip cursor={{ fill: "rgba(24,24,24,0.04)" }} content={<ChartTip suffix=" BS" />} />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]} label={{ position: "right", fontSize: 12, fontWeight: 700, fill: "var(--ink)" }}>
                      {teamBarData.map((d) => <Cell key={d.chef_id} fill={TEAM_BAR} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* ── Détail par équipe (cartes cliquables) ───────────────────── */}
            {chefId === "all" && byTeam.length > 0 && (
              <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-3 shadow-[var(--shadow-sm)]" data-testid="kpis-by-team">
                <div className="mb-2 text-sm font-extrabold text-ink">Par équipe</div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                  {byTeam.map((bt) => (
                    <button
                      key={bt.chef_id}
                      onClick={() => setChefId(bt.chef_id)}
                      className="min-w-0 rounded-xl border border-[var(--border-soft)] p-3 text-left transition-colors hover:border-[rgba(238,114,46,0.5)] hover:bg-[rgba(238,114,46,0.04)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]"
                      data-testid={`kpis-team-card-${bt.chef_id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-1.5">
                          <Building2 className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
                          <span className="truncate text-sm font-extrabold text-ink">{bt.chef_nom}</span>
                        </div>
                        <span className="shrink-0 rounded-full bg-[rgba(238,114,46,0.10)] px-2 py-0.5 text-xs font-extrabold text-[var(--shariti-orange)]">{_num(bt.team?.bulletins_comptes)} BS</span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11px]">
                        <KpiMemberStat label="Taux réel" value={_ratio(bt.team?.taux_reel)} tone={bt.team?.taux_reel != null ? "good" : "default"} />
                        <KpiMemberStat label="Validation" value={_pct(bt.team?.validation_rate)} />
                        <KpiMemberStat label="Annul. WC" value={_pct(bt.team?.welcome_call_rate)} tone={_wcTone(bt.team?.welcome_call_rate)} />
                        <KpiMemberStat label="Don moyen" value={_eur(bt.team?.don_moyen)} />
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <div className="flex -space-x-1.5">
                          {(bt.top || []).map((t) => (
                            <RecruiterAvatar key={t.rd_id} name={t.rd_name} src={rdPhotoSrc(t)} size={24} className="ring-2 ring-white" />
                          ))}
                        </div>
                        <span className="text-[10px] font-semibold text-muted-ink">{bt.size} collaborateur{bt.size > 1 ? "s" : ""}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Détail par collaborateur ────────────────────────────────── */}
            <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-3 shadow-[var(--shadow-sm)]" data-testid="kpis-members">
              <div className="mb-2 flex items-center gap-1.5 text-sm font-extrabold text-ink">
                <Trophy className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" /> Par collaborateur
                <span className="ml-auto text-[11px] font-semibold text-muted-ink">{members.length} affiché{members.length > 1 ? "s" : ""}</span>
              </div>
              {members.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-6 text-center text-sm text-muted-ink" data-testid="kpis-members-empty">
                  Aucun collaborateur ne correspond à ces filtres.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  {members.map((m) => (
                    <div key={m.rd_id} className="min-w-0 rounded-xl border border-[var(--border-soft)] p-3" data-testid={`kpi-member-${m.code_rd || m.rd_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <RecruiterAvatar name={m.rd_name} src={rdPhotoSrc(m)} size={40} />
                          <div className="min-w-0">
                            <div className="break-words text-sm font-bold text-ink">{m.rd_name}</div>
                            <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-semibold text-muted-ink">
                              {m.code_rd ? <span>{m.code_rd}</span> : null}
                              <span className="inline-flex items-center gap-1">
                                <span className="inline-block h-2 w-2 rounded-full" style={{ background: CANAL_COLORS[m.canal] || CANAL_COLORS.inconnu }} />
                                {m.canal_label}
                              </span>
                              <span className="rounded bg-[var(--surface-2)] px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide text-ink/70">{m.chef_nom}</span>
                              {m.is_independant ? <span className="rounded bg-[rgba(93,63,211,0.12)] px-1 py-0.5 text-[9px] font-bold uppercase text-[#5D3FD3]">indép.</span> : null}
                            </div>
                          </div>
                        </div>
                        <div className="shrink-0 rounded-full bg-[rgba(238,114,46,0.10)] px-2 py-0.5 text-xs font-extrabold text-[var(--shariti-orange)]">{_num(m.bulletins_comptes)} BS</div>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11px]">
                        <KpiMemberStat label="Taux réel (BS/h)" value={_ratio(m.taux_reel)} tone={m.taux_reel != null ? "good" : "default"} />
                        <KpiMemberStat label="Heures" value={m.heures_formation ? `${_heures(m.heures)} · dont ${_heures(m.heures_formation)} form.` : _heures(m.heures)} />
                        <KpiMemberStat label="Taux de validation" value={_pct(m.validation_rate)} />
                        <KpiMemberStat label="Annul. welcome call" value={_pct(m.welcome_call_rate)} tone={_wcTone(m.welcome_call_rate)} />
                        <KpiMemberStat label="Don moyen" value={_eur(m.don_moyen)} />
                        <KpiMemberStat label="Âge moyen" value={_age(m.average_age)} />
                        <KpiMemberStat label="Pré-attrition ≤3 m" value={m.attrition?.reliable ? _pct(m.attrition?.pre_attrition_rate) : (attrLoading ? "…" : "—")} tone={m.attrition?.reliable ? _attrTone(m.attrition?.pre_attrition_rate, t3) : "default"} />
                        <KpiMemberStat label="Attrition 12 m" value={m.attrition?.reliable ? _pct(m.attrition?.attrition_rate) : (attrLoading ? "…" : "—")} tone={m.attrition?.reliable ? _attrTone(m.attrition?.attrition_rate, t12) : "default"} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-2 text-[10px] leading-snug text-muted-ink">
                « Taux réel (BS/h) » = productivité = bulletins nets ÷ heures TERRAIN (formation exclue). « Taux de validation » = part des bulletins validés (approuvés + en attente) ÷ signés. « Annul. welcome call » = taux d'annulation à l'appel de bienvenue (cible ≤ 15 %). Le « canal » est déduit du code campagne des bulletins (dominant sur la période). Pré-attrition (≤ 3 mois) & attrition (12 mois) issues du Reporting — « — » tant qu'il n'y a pas assez de recul.
              </p>
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}
